/**
 * Offline /map-data cache in IndexedDB (Épico 39 / #10).
 * Falls back to in-memory Map when indexedDB is unavailable (tests / private mode).
 */

const DB_NAME = 'percurso-offline';
const DB_VERSION = 1;
const STORE = 'mapData';
const CACHE_PREFIX = 'newapp:offline-data:v1:';
const MAX_ENTRIES_PER_USER = 4;
const LEGACY_LS_PREFIX = 'newapp:offline-data:v1:';

/** @type {Map<string, object>} */
const memoryStore = new Map();

const userCacheKey = (user) => {
  const identity = user?.id ?? user?.username;
  return identity === undefined || identity === null ? null : encodeURIComponent(String(identity));
};

export const buildOfflineCacheKey = (user, filterSignature) => {
  const identity = userCacheKey(user);
  if (!identity) return null;
  return `${CACHE_PREFIX}${identity}:${filterSignature || 'default'}`;
};

const isValidEntry = (entry) => (
  entry && typeof entry === 'object' && entry.data && entry.quality
);

/** Optional ETag from last successful /map-data response (for If-None-Match). */
const readEtag = (entry) => (
  entry && typeof entry.etag === 'string' && entry.etag ? entry.etag : null
);

const idbAvailable = () => (
  import.meta.env.MODE !== 'test'
  && typeof indexedDB !== 'undefined'
  && indexedDB !== null
);

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('indexedDB open failed'));
  });
}

function idbRequest(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('indexedDB request failed'));
  });
}

async function idbGet(key) {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readonly');
    const row = await idbRequest(tx.objectStore(STORE).get(key));
    return row || null;
  } finally {
    db.close();
  }
}

async function idbPut(record) {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await idbRequest(tx.objectStore(STORE).put(record));
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('indexedDB abort'));
    });
  } finally {
    db.close();
  }
}

async function idbDelete(key) {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await idbRequest(tx.objectStore(STORE).delete(key));
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('indexedDB abort'));
    });
  } finally {
    db.close();
  }
}

async function idbKeysWithPrefix(prefix) {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readonly');
    const all = await idbRequest(tx.objectStore(STORE).getAll());
    return (all || [])
      .filter((row) => row && typeof row.key === 'string' && row.key.startsWith(prefix))
      .map((row) => ({ key: row.key, cachedAt: row.cachedAt || '' }));
  } finally {
    db.close();
  }
}

function clearLegacyLocalStorage(identity) {
  if (typeof localStorage === 'undefined') return;
  try {
    const prefix = `${LEGACY_LS_PREFIX}${identity}:`;
    Object.keys(localStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // privacy mode
  }
}

export const readOfflineData = async (user, filterSignature) => {
  const key = buildOfflineCacheKey(user, filterSignature);
  if (!key) return null;

  try {
    if (idbAvailable()) {
      const row = await idbGet(key);
      if (row && isValidEntry(row)) {
        return {
          data: row.data,
          quality: row.quality,
          cachedAt: row.cachedAt,
          etag: readEtag(row),
        };
      }
      return null;
    }
    const entry = memoryStore.get(key);
    if (!isValidEntry(entry)) return null;
    return {
      data: entry.data,
      quality: entry.quality,
      cachedAt: entry.cachedAt,
      etag: readEtag(entry),
    };
  } catch {
    const entry = memoryStore.get(key);
    if (!isValidEntry(entry)) return null;
    return {
      data: entry.data,
      quality: entry.quality,
      cachedAt: entry.cachedAt,
      etag: readEtag(entry),
    };
  }
};

export const writeOfflineData = async (user, filterSignature, payload) => {
  const key = buildOfflineCacheKey(user, filterSignature);
  if (!key || !payload?.data || !payload?.quality) return false;

  const identity = userCacheKey(user);
  const prefix = `${CACHE_PREFIX}${identity}:`;
  const record = {
    key,
    cachedAt: new Date().toISOString(),
    data: payload.data,
    quality: payload.quality,
    ...(typeof payload.etag === 'string' && payload.etag ? { etag: payload.etag } : {}),
  };

  const enforceLimit = async (listKeys, removeKey) => {
    const existing = await listKeys(prefix);
    if (!existing.some((row) => row.key === key) && existing.length >= MAX_ENTRIES_PER_USER) {
      const oldest = existing.sort((a, b) => a.cachedAt.localeCompare(b.cachedAt))[0];
      if (oldest) await removeKey(oldest.key);
    }
  };

  try {
    if (idbAvailable()) {
      await enforceLimit(idbKeysWithPrefix, idbDelete);
      await idbPut(record);
      clearLegacyLocalStorage(identity);
      return true;
    }

    await enforceLimit(
      async (p) => [...memoryStore.keys()]
        .filter((k) => k.startsWith(p))
        .map((k) => ({ key: k, cachedAt: memoryStore.get(k)?.cachedAt || '' })),
      async (k) => { memoryStore.delete(k); }
    );
    memoryStore.set(key, record);
    return true;
  } catch {
    try {
      memoryStore.set(key, record);
      return true;
    } catch {
      return false;
    }
  }
};

export const clearOfflineData = async (user) => {
  const identity = userCacheKey(user);
  if (!identity) return;
  const prefix = `${CACHE_PREFIX}${identity}:`;

  try {
    if (idbAvailable()) {
      const rows = await idbKeysWithPrefix(prefix);
      await Promise.all(rows.map((row) => idbDelete(row.key)));
    }
    [...memoryStore.keys()].filter((k) => k.startsWith(prefix)).forEach((k) => memoryStore.delete(k));
    clearLegacyLocalStorage(identity);
  } catch {
    [...memoryStore.keys()].filter((k) => k.startsWith(prefix)).forEach((k) => memoryStore.delete(k));
  }
};

/** Test helper — wipe memory + attempt IDB clear for this process. */
export const __resetOfflineCacheForTests = async () => {
  memoryStore.clear();
  if (!idbAvailable()) return;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    await idbRequest(tx.objectStore(STORE).clear());
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // ignore
  }
};
